import {
  Enum_Translation_Locale,
  Enum_Translation_Type,
  InputMaybe,
  WatchQueryFetchPolicy,
  isNotEmpty,
  notNull,
  useTranslationsLazyQuery,
} from '@cyren/common-lib';
import produce from 'immer';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useIsV2Path } from '../app/route-utils';
import { convTrans } from '../gql-tools/utils/convert';
import { isEqualTrans } from '../models/translation';
import { Keys } from '../patient/patient-types';
import type { TTranslation } from '../graphql-enhanced';
import { translationControllerSearch } from '../v2/api/generated/translation/translation';
import type { SearchTranslationResponseDto } from '../v2/api/generated/types/searchTranslationResponseDto';
import { AppStateCookie } from '../app/use-app-state';
import { PaDataRepoState } from './use-data';

export function usePatientTransData({
  keys,
  locale,
}: { locale?: Enum_Translation_Locale; keys?: InputMaybe<Keys> } = {}) {
  const [appStateCookie] = useRecoilState(AppStateCookie);
  const localeKey = locale || appStateCookie?.localeKey || 'english';
  const isV2Path = useIsV2Path();

  const [dataState, setDataState] = useRecoilState(PaDataRepoState);
  const { survey, translations } = dataState;

  const [dataTransV2, setDataTransV2] = useState<SearchTranslationResponseDto | undefined>(undefined);
  const [loadingTransV2, setLoadingTransV2] = useState(false);

  const keysNotFound = useMemo(() => {
    return keys
      ?.filter((key) => {
        const found = translations?.find((tr) => tr.key === key && tr.locale === localeKey);
        if (!found) return key;
        return null;
      })
      .filter(notNull)
      .sort();
  }, [keys, translations, localeKey]);

  const mergeTranslationsIntoState = (items?: TTranslation[]) => {
    if (!items?.length) return;

    setDataState((st) => {
      // Check if there are any new items before producing a new state
      const hasNewItems = items.some((item) => {
        const existing = st.translations.find((t) => isEqualTrans({ trans1: t, trans2: item }));
        return !existing;
      });
      if (!hasNewItems) return st;

      return produce(st, (draft) => {
        items.forEach((item) => {
          const idx = draft.translations.findIndex((t) => isEqualTrans({ trans1: t, trans2: item }));

          if (idx === -1) {
            draft.translations.push(item);
          }
        });
      });
    });
  };

  const [fetchTransGql, { data: dataTransGql, loading: loadingTransGql }] = useTranslationsLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      const list = data?.translations?.data?.map(convTrans);
      mergeTranslationsIntoState(list);
    },
  });

  const fetchTrans = async ({
    fetchPolicy,
    asNeededKeys,
  }: {
    fetchPolicy?: WatchQueryFetchPolicy;
    asNeededKeys?: string[];
  } = {}) => {
    if (survey?.id == null || (isEmpty(keysNotFound) && isEmpty(asNeededKeys))) {
      return undefined;
    }

    const keysToFetch = isEmpty(asNeededKeys) ? keysNotFound : asNeededKeys;

    if (isV2Path) {
      try {
        setLoadingTransV2(true);
        const response = await translationControllerSearch({
          surveyId: parseInt(survey.id, 10),
          locale: localeKey,
          type: 'patient',
          keys: keysToFetch,
          limit: 10000,
        });
        setDataTransV2(response);

        const list: TTranslation[] =
          response?.translations?.map((item) => ({
            id: `${item.id}`,
            key: item.key,
            value: item.value,
            locale: item.locale,
            dataType: item.dataType,
            type: item.type,
          } as TTranslation)) || [];

        mergeTranslationsIntoState(list);
        return response;
      } finally {
        setLoadingTransV2(false);
      }
    }

    return fetchTransGql({
      fetchPolicy,
      variables: {
        pagination: {
          limit: 10000,
        },
        filters: {
          type: {
            eq: Enum_Translation_Type.Patient,
          },
          locale: {
            eq: localeKey,
          },
          key: {
            in: keysToFetch,
          },
          survey_id: {
            eq: parseInt(survey?.id, 10),
          },
        },
      },
    });
  };

  const keysNotFoundKey = useMemo(() => JSON.stringify(keysNotFound || []), [keysNotFound]);
  const lastFetchSignatureRef = useRef<string>('');

  // selected treeId
  useEffect(() => {
    const signature = `${localeKey}|${keysNotFoundKey}|${survey?.id}|${isV2Path}`;
    if (lastFetchSignatureRef.current === signature) return;
    lastFetchSignatureRef.current = signature;
    fetchTrans();
  }, [localeKey, keysNotFoundKey, survey?.id, isV2Path]);

  const activeDataTrans = isV2Path ? dataTransV2 : dataTransGql;
  const activeLoadingTrans = isV2Path
    ? loadingTransV2
    : loadingTransGql || (isNotEmpty(keysNotFound) && !dataTransGql?.translations);

  return [
    {
      dataState,
      dataTrans: activeDataTrans,
      loadingTrans: activeLoadingTrans,
    },
    {
      fetchTrans,
    },
  ] as const;
}
